import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModulesService, Module } from '../core/api/modules.service';
import { BreakGlassService } from '../core/auth/break-glass.service';
// 0.62.0.2: FakeAuthService entfernt
import { FatipOidcAuthService } from '../core/auth/fatip-oidc.service';
import { EmptyStateComponent } from '../core/ui/empty-state.component';
import { FapIconComponent } from '../core/ui/fap-icon.component';

const FAV_STORAGE = 'fap.favourites';

/**
 * 0.62.x — Public-Module-Allowlist für anonyme Visitor.
 *
 * Wenn der User noch nicht echt eingeloggt ist (kein OIDC, kein BG),
 * sieht er auf der Modules-Landing nur diese 2 Kacheln. Gezählt sind die
 * aktiven Einträge des Sets ('apis' und 'integration-guidelines'), nicht
 * die auskommentierten IDs darunter, die frühere Cuts entfernt haben.
 * Alles andere wird client-side ausgefiltert. Begründung: für Stakeholder-
 * Demos sollen nur die 'öffentlichen' Inhalte sichtbar sein, nicht der
 * Operations-Bereich.
 *
 * Die IDs müssen mit den Backend-Module-IDs (services/org modules-Catalog)
 * matchen. Wenn Backend einen anderen Slug liefert, hier ergänzen.
 */
const ANONYMOUS_VISIBLE_MODULES = new Set<string>([
  'apis',                  // API-Katalog
  'integration-guidelines', // Integration Guidelines
  // Cut K4 (BDR-034/BDR-037): 'dev-dashboard' entfernt — das Modul ist weg und
  // der Backend-Catalog konnte die ID ohnehin nie liefern.
  // 0.61.19.9: 'roadmap' aus dem Set entfernt — Tile wurde aus dem Backend-
  // Catalog gelöscht. Route /modules/roadmap bleibt aber für Direkt-Links
  // erreichbar (Versions-Chip im Header öffnet Release-Modal).
  // BDR-028 (WS3): 'tech-stack' aus dem Set entfernt — Modul entfernt (E6).
]);

/**
 * 0.57.36 — Per-Modul-ID-Icon-Overrides nach User-Feedback.
 *
 * Backend liefert grobe Tile-Keys (building/user/inbox/...) die in
 * iconFor() generisch gemappt werden. Manche Module brauchen aber
 * spezifischere Icons als der Backend-Key suggeriert. Diese Map ist
 * der hand-kuratierte Override.
 *
 * Wird zuerst geprüft; wenn kein Eintrag, fällt iconFor() auf den
 * generischen Map-Eintrag zurück.
 */
const TILE_ICON_BY_MODULE_ID: Record<string, string> = {
  // Hot-Path-User-Feedback 2026-04-26:
  // Cut K4 (BDR-034/BDR-037): 'tasks': 'briefcase' entfernt. Die Kachel fiel
  // in 0.57.67 (W-20) aus dem Backend-Catalog, und die Tile-Liste kommt
  // ausschliesslich von dort — ein Direkt-Link rendert keine Tile, der
  // Eintrag konnte also seit 0.57.67 nicht mehr greifen. Das Brief-Icon im
  // Header bezieht sein Icon ohnehin separat aus task-inbox.component.ts.
  'bulk-onboarding': 'cloud_upload',
  // BDR-028 (WS3): 'tech-stack' + 'datamodel' Icon-Overrides entfernt (E6).
  'docs': 'easy-language',           // FATIP API Dokumentation
  // UX-1 (Mandate-Graph) Task 3: Modul-ID 'mandate-graph' zu 'mandate'
  // umbenannt (Nachfolger-Modul "Mandatsgraph"). Das Icon steht fuer das
  // verbindende Konzept (Person ↔ Mandat ↔ Org ↔ Permission ↔ Audit).
  // T13 (0.72.24): Sprite-Key von 'share-nodes' auf 'share' korrigiert.
  // 'share-nodes' steht nicht in oblique-icons.svg. Das Tile rendert ein
  // leeres Icon, seit dieser Eintrag den Wert traegt: gesetzt am 2026-07-20
  // in 0448cad unter dem damaligen Schluessel 'mandate-graph', der zu dem
  // Zeitpunkt eine lebende Modul-ID war. Die Umbenennung des Schluessels am
  // 2026-07-22 (351822a) hat den Wert nicht angefasst und ist nicht die
  // Ursache.
  // Regel fuer jeden weiteren Override: iconFor() gibt einen vorhandenen
  // Eintrag dieser Map direkt zurueck und erreicht das generic-Mapping nur,
  // wenn KEIN Override existiert. Ein Override schaltet den Fallback also
  // ab; eine fehlende Sprite-ID rendert ein leeres <use> ohne Fehler.
  'mandate': 'share',
  'identities': 'address-book',      // „Benutzer"
  'system-tools': 'grid',
  'my-account': 'person',
  // 0.57.41-followup: Mandate (ADR-024). Backend liefert auch
  // 'justice-scales', hier explizit für Konsistenz mit dem Override-
  // Pattern.
  'mandates': 'justice-scales',
  // 0.61.19.10 — Backend liefert seit 0.61.19.9 'shield_lock' bzw. 'apps' für
  // diese Module, aber die Sprite-IDs sind nicht im generic-Mapping unten →
  // ohne diese Overrides fielen beide auf 'apps'-Default zurück (= 9-Punkt-
  // Grid für PAM, Bug-Befund 2026-05-03). Override-Pattern (Codex Q2 GO)
  // ist sauberer als generic-Passthrough weil jedes künftige Backend-Icon-
  // Feld im Frontend bewusst gemapped werden muss.
  'pam':      'shield_lock',
  'applications': 'apps',
  // PAM Cut 3 T3 — Cloud-Governance-Tile. Sprite-ID 'cloud' ist nicht
  // im generic-Mapping → ohne Override fiele das Tile auf 'apps' zurück.
  'cloud-governance': 'cloud',
  // Cut P5e1 (Issue #781) — Agent-Registry-Tile. 'apps' wie die
  // Applications-Tile: Agents sind Machine-Clients (ADR-993), keine
  // Personen. Der Override hält die Wahl bewusst statt still vom
  // 'apps'-Default abzuhängen (Muster wie 'mandates' oben).
  'agents': 'apps',
};

/**
 * Client-side domain grouping for the module catalog (0.41.4).
 *
 * The backend `/v1/portal/modules` doesn't carry a category yet. Mapping
 * here keeps this as pure frontend polish; a follow-up release can move
 * the categorisation into the service response when we grow the catalog
 * beyond what's easy to maintain in a literal.
 *
 * Unknown ids fall into `other` so the landing page still renders when
 * a new module lands before this map catches up.
 */
export type Category = 'identity' | 'access' | 'pki' | 'governance' | 'operations' | 'resources' | 'other';

const CATEGORY_BY_ID: Record<string, Category> = {
  identities: 'identity',
  // 0.57.8 — Bulk-Onboarding ist neu Platform-Admin-only (ADR-015 +
  // platformAdminGuard); rückt aus 'identity' in 'operations' (System-
  // Tools-Sektion). Operative Org-CSV-Pflege läuft über die Daten-
  // anlieferung im Org-Detail.
  'bulk-onboarding': 'operations',
  'developer-reviews': 'identity',
  'my-account': 'identity',

  organisations: 'access',
  applications: 'access',
  apis: 'access',

  // ADR-146 (WS2a, CA/PKI-Descope): zertifikate-mgmt / ca-hierarchy /
  // issuance-workflow Module + ihre 'pki'-Mappings entfernt (PKI geparkt,
  // BDR-028 E4). BDR-028 (WS3): audit-center-Mockup ebenfalls entfernt.
  // Cut K4 (BDR-037): oauth-inspector entfernt, der letzte 'pki'-Eintrag.
  // Die Kategorie bleibt in Category und CATEGORY_ORDER deklariert, hat aber
  // kein Mapping mehr; groups() laesst leere Buckets weg.

  audit: 'governance',
  roadmap: 'governance',
  // PAM Cut 3 T7 — beide Module unter Governance statt 'other'-Fallback:
  // pam (Data-Plane: JIT-Anträge/Sessions/Approvals) + cloud-governance
  // (Control-Plane: Accounts/Approver/JIT-Targets, Platform-Admin).
  'pam': 'governance',
  'cloud-governance': 'governance',
  // Cut P5e1 (Issue #781) — Agent-Registry unter Governance statt
  // 'other'-Fallback: dieselbe Plattform-Admin-Kategorie wie pam +
  // cloud-governance (CatPlatformAdmin im Backend-Modulkatalog).
  'agents': 'governance',

  // Cut K4 (BDR-034/BDR-037): tasks: 'operations' entfernt — dieselbe tote
  // Modul-ID wie im Icon-Override oben, seit 0.57.67 nicht mehr im Catalog.
  // 'workflows' entfernt mit 0.62.1.19b (n8n-Orchestrator-Stub raus)
  'system-tools': 'operations',
  notifications: 'operations',

  'developer-portal': 'resources',
  docs: 'resources',
  // BDR-028 (WS3): tech-stack + datamodel Resources-Mappings entfernt (E6).
  // UX-1 (Mandate-Graph) Task 3: Modul-ID 'mandate-graph' zu 'mandate'
  // umbenannt (echte Sicht auf die eigenen APIs, kein FE-only-Mock mehr),
  // Resources-Bucket unveraendert beibehalten (gleicher Referenz-Charakter
  // wie zuvor, jetzt zusaetzlich mit produktivem Karten-Grid + Graph).
  'mandate': 'resources',

  // 0.42.0: Platform-Verwaltung — sits under Governance because it
  // manages the platform-role grants (who's an admin, who's an operator).
  platform: 'governance',
};

/** Display order — identity first (most entered), resources last. */
const CATEGORY_ORDER: Category[] = [
  'identity', 'access', 'pki', 'governance', 'operations', 'resources', 'other',
];

interface CategoryGroup {
  key: Category;
  modules: Module[];
}

@Component({
  selector: 'fap-landing',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslateModule, EmptyStateComponent, FapIconComponent],
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss']
})
export class LandingComponent {
  private svc = inject(ModulesService);
  private t   = inject(TranslateService);
  private router = inject(Router);
  // 0.62.0.2: FakeAuthService raus (Persona-Switcher-Removal Step 2/3).
  private oidc = inject(FatipOidcAuthService);
  private bg = inject(BreakGlassService);

  isAnonymous = computed(() => !this.oidc.authenticated() && !this.bg.isActive());

  modules = signal<Module[]>([]);
  /** Cut U3: true from construction until the first response arrives, so the
   *  first paint is a loading line rather than the zero-results paint. A
   *  refetch that already has tiles leaves this false and keeps them on
   *  screen; `load()` says when. */
  loading = signal(true);
  /** Cut U3: set when the catalog request failed. Kept apart from an empty
   *  `modules()` because the two say different things to the reader. */
  loadFailed = signal(false);
  /** Cut U37: the `notice` query parameter the availability guard leaves
   *  behind when it redirects a disabled module back to this catalog
   *  (`module_disabled` is the only value today). Cut U58: a signal, so the
   *  guard covers both dispositions. The seed from the router state snapshot
   *  covers the created-after-the-navigation case (the snapshot read is right
   *  for it, and the fixture-based specs rely on it — no ActivatedRoute is
   *  injected, the reason U37 recorded); the NavigationEnd subscription in
   *  the constructor covers a redirect that arrives while the component is
   *  alive (route reuse), where the once-only property read this replaces was
   *  stale and the notice never rendered. The no-ActivatedRoute reason stands
   *  for both: the `modules` route instantiates this component directly, and
   *  the fixture-based specs create it after the navigation, where an
   *  ActivatedRoute inject would fail. */
  notice = signal(this.router.routerState.snapshot.root.queryParamMap.get('notice'));
  query = signal('');
  // Cut U6 (task 6): `mandant` stood here and no computed on this page read
  // it. It went with the `<select>` in the toolbar that wrote it.
  onlyFavourites = signal(false);
  sortAsc = signal(true);
  view = signal<'grid' | 'list'>('grid');
  favourites = signal<Set<string>>(this.loadFavs());

  /**
   * Cut U3 — the catalog this reader may see at all, before the search box
   * and the favourites chip narrow it.
   *
   * 0.62.x — Anonymous-Filter: für nicht-eingeloggte Visitor zeige
   * nur die kuratierte Allowlist oben (heute API-Katalog und Integration
   * Guidelines). Alles andere ist Operations-Bereich und braucht Login.
   *
   * This is the baseline the template measures "no match" against: zero here
   * means the catalog is empty, while zero in `visible()` with entries here
   * means the reader's own filter matched nothing. Before this cut both read
   * as `no_match`, so an empty catalog blamed the reader's search.
   */
  catalogItems = computed<Module[]>(() => {
    const items = this.modules();
    return this.isAnonymous()
      ? items.filter(m => ANONYMOUS_VISIBLE_MODULES.has(m.id))
      : items;
  });

  visible = computed(() => {
    const q = this.query().trim().toLowerCase();
    const lang = this.t.currentLang || 'de';
    const favs = this.favourites();
    let items = this.catalogItems().slice();

    if (q.length >= 3) {
      items = items.filter(m =>
        (m.title_i18n[lang] ?? '').toLowerCase().includes(q) ||
        (m.description_i18n[lang] ?? '').toLowerCase().includes(q) ||
        m.id.toLowerCase().includes(q)
      );
    }
    if (this.onlyFavourites()) {
      items = items.filter(m => favs.has(m.id));
    }
    items.sort((a, b) => {
      const aFav = favs.has(a.id) ? 0 : 1;
      const bFav = favs.has(b.id) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      const at = a.title_i18n[lang] ?? a.id;
      const bt = b.title_i18n[lang] ?? b.id;
      return this.sortAsc() ? at.localeCompare(bt) : bt.localeCompare(at);
    });
    return items;
  });

  /** Favourites pinned at the top so the user reaches their most-used modules
   *  with one scroll irrespective of category. When the user has no favourites
   *  yet, this section simply hides (empty array). */
  favouriteModules = computed(() => {
    const favs = this.favourites();
    return this.visible().filter(m => favs.has(m.id));
  });

  /** 0.57.46 ADR-027 — Flat-Tile-Grid statt kategorisierter Sektionen.
   *
   *  Reihenfolge:
   *    1. Favoriten zuerst (vom User markiert mit ★) — pinned
   *    2. Restliche Module in Sort-Order (alphabetisch ASC oder DESC,
   *       gesteuert via toggleSort())
   *
   *  Begründung: Sektionen mit Headers ("Identitäten", "Zugriff &
   *  Anwendungen") forcen eine künstliche Taxonomie, die User-Mental-
   *  Models nicht trifft und unnötiges Scrolling erzeugt. Ein flaches
   *  Grid mit Favoriten-Pin gibt Schnellzugriff ohne Sektion-Lärm.
   *  Die alte CATEGORY_ORDER + CATEGORY_BY_ID-Konstanten bleiben im
   *  Code für Such-/Filter-Erweiterungen ungenutzt; können in einem
   *  späteren Cleanup gelöscht werden.
   */
  flatModules = computed<Module[]>(() => {
    const favs = this.favourites();
    const all = this.visible();
    const pinned: Module[] = [];
    const rest: Module[] = [];
    for (const m of all) {
      if (favs.has(m.id)) pinned.push(m);
      else rest.push(m);
    }
    return [...pinned, ...rest];
  });

  /** @deprecated 0.57.46 — Sektionen entfernt, siehe ADR-027. flatModules() ersetzt das.
   *  Computed bleibt vorerst für Test-/Debug-Zwecke; nicht mehr im Template referenziert. */
  groups = computed<CategoryGroup[]>(() => {
    const bucket = new Map<Category, Module[]>();
    for (const m of this.visible()) {
      const cat = CATEGORY_BY_ID[m.id] ?? 'other';
      if (!bucket.has(cat)) bucket.set(cat, []);
      bucket.get(cat)!.push(m);
    }
    const out: CategoryGroup[] = [];
    for (const key of CATEGORY_ORDER) {
      const modules = bucket.get(key);
      if (modules && modules.length > 0) out.push({ key, modules });
    }
    return out;
  });

  constructor() {
    // 0.62.0.2: Re-fetch whenever the OIDC subject changes (login/logout).
    // External-Dev-Auto-Redirect-Pfad (vorher persona='EXTERNAL_DEV_ONLY')
    // entfaellt - die Persona laesst sich aus dem OIDC-Token nicht
    // direkt ableiten. Cut K4 (BDR-034/BDR-037): das External-Dev-Dashboard
    // gibt es nicht mehr; eingeloggte External-Devs arbeiten ueber den
    // normalen Tile-Grid auf /modules.
    effect(() => {
      this.oidc.subject(); // track
      // Cut U3 review: the OIDC subject is the ONLY dependency of this
      // effect. `load()` reads `modules()` to decide whether to show the
      // loading line, and it writes `modules()` when the answer arrives, so
      // a tracked call makes the effect its own trigger and the browser tab
      // runs out of memory. Measured: the spec case for a refetch took the
      // vitest worker down with a heap overflow until this wrapper went in.
      untracked(() => this.load());
    });
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.notice.set(this.router.routerState.snapshot.root.queryParamMap.get('notice')));
  }

  /**
   * Cut U3 — the catalog load, as a method rather than an inline subscription.
   *
   * The effect above owns WHEN it runs (on construction and whenever the OIDC
   * subject changes). The retry button needs the same load without a subject
   * change, and an effect cannot be re-armed from a click handler, so the body
   * lives here and both callers reach it. Writing signals from inside an
   * effect is supported in Angular 20; the response branches run
   * asynchronously anyway.
   *
   * The loading line is for a page that has nothing to show: `loading` is
   * set only while `modules()` is empty, which is the first load and any
   * load after a failed one. A refetch that follows a success leaves the
   * tiles on screen and swaps them when the answer arrives, the way the page
   * behaved before this cut had a loading flag at all.
   *
   * On failure the list is cleared as before AND `loadFailed` is set, so the
   * template shows a load-failed message with a retry instead of the
   * zero-results sentence.
   */
  load(): void {
    if (this.modules().length === 0) {
      this.loading.set(true);
    }
    this.loadFailed.set(false);
    this.svc.list().subscribe({
      next: r => {
        this.modules.set(r.modules);
        this.loading.set(false);
      },
      error: () => {
        this.modules.set([]);
        this.loadFailed.set(true);
        this.loading.set(false);
      },
    });
  }

  // 0.62.0.2: alreadyLandedFor / markLanded entfernt - sie waren nur
  // fuer den persona='EXTERNAL_DEV_ONLY'-Auto-Redirect-Pfad. Mit
  // FakeAuthService-Removal entfaellt die Persona-Discrimination.

  title(m: Module): string {
    return m.title_i18n[this.t.currentLang || 'de'] ?? m.title_i18n['de'] ?? m.id;
  }
  desc(m: Module): string {
    return m.description_i18n[this.t.currentLang || 'de'] ?? '';
  }

  isFav(m: Module) { return this.favourites().has(m.id); }

  toggleFav(m: Module, ev: Event) {
    ev.stopPropagation();
    ev.preventDefault();
    const next = new Set(this.favourites());
    if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
    this.favourites.set(next);
    try {
      localStorage.setItem(FAV_STORAGE, JSON.stringify([...next]));
    } catch { /* ignore */ }
  }

  open(m: Module) {
    if (!m.enabled) return;
    this.router.navigateByUrl(m.entry_route);
  }

  toggleSort() { this.sortAsc.set(!this.sortAsc()); }

  // Cut U25 (L94): the way back out returns the reader to the full catalog.
  // Since U54 the favourites chip has its own state arm with its own way
  // back out, so the query is the only thing this clears.
  clearSearch() {
    this.query.set('');
  }

  /**
   * 0.57.35 / .36: Oblique-Icon-Mapping für die Modul-Tile-Icons.
   *
   * Pro Modul wird zuerst geprüft ob es einen explizit-konfigurierten
   * Per-ID-Override gibt (TILE_ICON_BY_MODULE_ID, vom User-Feedback
   * 2026-04-26 gepflegt) — sonst Fallback auf den generischen
   * Backend-Tile-Key ('building'/'user'/etc.).
   *
   * Wenn beide nicht greifen → 'apps' als generisches Modul-Symbol.
   */
  iconFor(_name: string, moduleId?: string): string {
    if (moduleId && TILE_ICON_BY_MODULE_ID[moduleId]) {
      return TILE_ICON_BY_MODULE_ID[moduleId];
    }
    const generic: Record<string, string> = {
      building: 'building',
      user: 'person',
      package: 'archive-box',
      key: 'key',
      link: 'link',
      shield: 'shield_checkmark',
      compass: 'compass',
      inbox: 'inbox',
      chart: 'chart_bar_vertical',
      check: 'checkmark_circle',
      'shield-check': 'shield_checkmark',
      settings: 'settings',
      workflow: 'lightning',
      book: 'book',
      'check-list': 'file_checkmark',
      wrench: 'wrench',
      tag: 'tag',
      // T13 (0.72.24), Ruling 7: Das Backend liefert fuer
      // 'integration-guidelines' den Namen 'rocket'. Ohne Eintrag fiel die
      // Kachel auf das 'apps'-Raster zurueck. Das Sprite kennt keine Rakete;
      // 'airplane_takeoff' ist seine Vokabel fuer denselben Start-Gedanken.
      // Der zweite ungemappte Backend-Name, 'share-nodes' (Modul 'mandate'),
      // braucht hier KEINEN Eintrag: 'mandate' hat einen Override in
      // TILE_ICON_BY_MODULE_ID und erreicht dieses generic-Mapping nie.
      rocket: 'airplane_takeoff',
    };
    return generic[_name] ?? 'apps';
  }

  private loadFavs(): Set<string> {
    try {
      const raw = localStorage.getItem(FAV_STORAGE);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
    // ADR-146 (WS2a): Default-Favourite war 'certificates' (CA-Modul entfernt)
    // → auf ein Keeper-Modul umgestellt.
    return new Set(['organisations']);
  }
}
